import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Card, message, Spin, Typography} from 'antd';
import {InfoCircleOutlined, RobotOutlined} from '@ant-design/icons';
import BotStatus from '../../pages/bot/BotStatus';
import BotControl from '../../pages/bot/BotControl';
import PromptEditor from '../../components/bot/PromptEditor';
import PromptHistory from '../../pages/bot/PromptHistory';
import PromptFlowProgressBoard from '../../pages/bot/PromptFlowProgressBoard';
import {botService} from '../../services/botService';
import {apiConnectionService} from '../../services/apiConnectionService';
import {useAgentWebSocket} from '../../hooks/useAgentWebSocket';
import {useI18n} from '../../i18n/useI18n';
import './PocBotView.css';

const {Title, Text} = Typography;

interface BotStatusType {
    callCount: number;
    lastCallTime: number | null;
    modelName: string;
    apiKeyId: number;
    apiKeyDescription: string;
    riskControlMode: string;
    tradingStyle: string;
    automaticTradeEnabled: boolean;
    systemStatus: string;
    lastProcessingTime: number;
}

const PocBotView: React.FC = () => {
    const {t} = useI18n();
    const isPublicView = true;
    const [botStatus, setBotStatus] = useState<BotStatusType | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedApiKeyId, setSelectedApiKeyId] = useState<number | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [promptGenerateTrigger, setPromptGenerateTrigger] = useState(0);
    const [screenSize, setScreenSize] = useState<'large' | 'medium' | 'small'>('large');
    const [triggering, setTriggering] = useState(false);
    const [generatingPrompt, setGeneratingPrompt] = useState(false);
    const [showEquityModal, setShowEquityModal] = useState(false);
    const [chainModalVisible, setChainModalVisible] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
    const [botControlCollapsed, setBotControlCollapsed] = useState(false);

    const hasOpenModal = showEquityModal || chainModalVisible;
    // WebSocket 实时进度
    const {connected: wsConnected, stepsByRun} = useAgentWebSocket(
        selectedApiKeyId,
        !hasOpenModal
    );

    const handleTransferToChat = (sessionId: number, userId: string) => {
        window.dispatchEvent(new CustomEvent('chat-transfer', {
            detail: {sessionId, userId: userId || 'default'}
        }));
        message.success('已转移到AI助手');
    };

    const fetchBotStatus = useCallback(async (apiKeyId?: number, isBackground = false) => {
        try {
            if (!isBackground) {
                setLoading(true);
            }
            const response = await botService.getBotStatus(apiKeyId);
            if (response.success && response.data) {
                setBotStatus(response.data);
                if (null === selectedApiKeyId && response.data.apiKeyId) {
                    setSelectedApiKeyId(response.data.apiKeyId);
                }
            }
        } catch {
            // 静默处理
        } finally {
            if (!isBackground) {
                setLoading(false);
            }
        }
    }, [selectedApiKeyId]);

    const handleTriggerBot = async (apiKeyId: number): Promise<boolean> => {
        try {
            setTriggering(true);
            const response = await botService.triggerBot(apiKeyId);
            if (response.success) {
                message.success('BOT触发成功');
                setRefreshTrigger(prev => prev + 1);
                fetchBotStatus(apiKeyId);
                return true;
            } else {
                message.error(response.message || 'BOT触发失败');
                return false;
            }
        } catch {
            message.error('BOT触发失败');
            return false;
        } finally {
            setTriggering(false);
        }
    };

    const handleApiKeyChange = (apiKeyId: number) => {
        setSelectedApiKeyId(apiKeyId);
        fetchBotStatus(apiKeyId);
    };

    const handleGeneratePrompt = (apiKeyId: number) => {
        setPromptGenerateTrigger(prev => prev + 1);
    };

    const handlePromptGenerated = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    useEffect(() => {
        const initApiKey = async () => {
            try {
                const result = await apiConnectionService.getGlobalDisplayConnection();
                const keyId = result?.apiKeyId || null;
                if (keyId) {
                    setSelectedApiKeyId(keyId);
                }
                fetchBotStatus(keyId || undefined);
            } catch {
                fetchBotStatus();
            }
        };
        initApiKey();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width < 1024) {
                setScreenSize('small');
            } else if (width < 1600) {
                setScreenSize('medium');
            } else {
                setScreenSize('large');
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (hasOpenModal) {
            return;
        }
        const interval = setInterval(() => {
            if (selectedApiKeyId) {
                fetchBotStatus(selectedApiKeyId, true);
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [selectedApiKeyId, hasOpenModal, fetchBotStatus]);

    return (
        <div className="poc-bot-view">
            <div className="poc-bot-view-header">
                <Title level={4} className="poc-bot-view-title">
                    <RobotOutlined style={{marginRight: 8, color: 'var(--gold)'}}/>
                    {t('site.pocBotView.title')}
                </Title>
                <Text className="poc-bot-view-subtitle">
                    {t('site.pocBotView.subtitle')}
                </Text>
            </div>

            <Alert
                className="poc-bot-view-notice"
                description={t('site.pocBotView.notice')}
                type="info"
                showIcon
                icon={<InfoCircleOutlined/>}
            />

            {loading ? (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200}}>
                    <Spin size="large"/>
                </div>
            ) : (
                <>
                    <Card
                        style={{
                            backgroundColor: '#0C1120',
                            border: '1px solid rgba(255,255,255,0.13)',
                            borderRadius: '8px',
                            marginBottom: '16px'
                        }}
                        styles={{
                            body: {
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center'
                            }
                        }}
                    >
                        {botStatus ? (
                            <BotStatus
                                status={botStatus}
                                showEquityModal={showEquityModal}
                                onOpenEquityModal={() => setShowEquityModal(true)}
                                onCloseEquityModal={() => setShowEquityModal(false)}
                                readOnly={isPublicView}
                            />
                        ) : (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                color: 'rgba(255, 255, 255, 0.45)',
                                fontSize: '14px'
                            }}>
                                暂无BOT状态信息，请检查API Key配置
                            </div>
                        )}
                    </Card>

                    <div style={{marginBottom: '16px'}}>
                        <PromptFlowProgressBoard
                            apiKeyId={selectedApiKeyId}
                            refreshTrigger={refreshTrigger}
                            wsConnected={wsConnected}
                            stepsByRun={stepsByRun}
                        />
                    </div>

                    <div style={{
                        display: screenSize === 'small' ? 'block' : 'flex',
                        gap: '16px',
                        minHeight: 0
                    }}>
                        {screenSize === 'small' ? (
                            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                                <div style={{minHeight: botControlCollapsed ? '80px' : '180px'}}>
                                    <BotControl
                                        selectedApiKeyId={selectedApiKeyId}
                                        onTriggerBot={handleTriggerBot}
                                        onApiKeyChange={handleApiKeyChange}
                                        onGeneratePrompt={handleGeneratePrompt}
                                        refreshTrigger={refreshTrigger}
                                        botStatus={botStatus}
                                        collapsed={botControlCollapsed}
                                        setCollapsed={setBotControlCollapsed}
                                        generatingPrompt={generatingPrompt}
                                        publicView={isPublicView}
                                    />
                                </div>
                                <div style={{minHeight: '240px', position: 'relative'}}>
                                    <PromptEditor
                                        apiKeyId={selectedApiKeyId}
                                        onPromptGenerated={handlePromptGenerated}
                                        triggerGenerate={promptGenerateTrigger}
                                        onGeneratingChange={setGeneratingPrompt}
                                    />
                                </div>
                                <div style={{minHeight: '200px', position: 'relative'}}>
                                    <PromptHistory
                                        apiKeyId={selectedApiKeyId}
                                        refreshTrigger={refreshTrigger}
                                        chainModalVisible={chainModalVisible}
                                        onOpenChainModal={(recordId) => {
                                            setSelectedRecordId(recordId);
                                            setChainModalVisible(true);
                                        }}
                                        onCloseChainModal={() => {
                                            setChainModalVisible(false);
                                            setSelectedRecordId(null);
                                        }}
                                        selectedRecordId={selectedRecordId}
                                        onTransferToChat={handleTransferToChat}
                                        publicView={true}
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={{
                                    flex: '2 2 40%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                    minWidth: screenSize === 'medium' ? '400px' : '440px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{minHeight: botControlCollapsed ? '80px' : '180px'}}>
                                        <BotControl
                                            selectedApiKeyId={selectedApiKeyId}
                                            onTriggerBot={handleTriggerBot}
                                            onApiKeyChange={handleApiKeyChange}
                                            onGeneratePrompt={handleGeneratePrompt}
                                            refreshTrigger={refreshTrigger}
                                            botStatus={botStatus}
                                            collapsed={botControlCollapsed}
                                            setCollapsed={setBotControlCollapsed}
                                            generatingPrompt={generatingPrompt}
                                            publicView={isPublicView}
                                        />
                                    </div>
                                    <div style={{flex: '1 1 auto', minHeight: '320px', position: 'relative'}}>
                                        <PromptEditor
                                            apiKeyId={selectedApiKeyId}
                                            onPromptGenerated={handlePromptGenerated}
                                            triggerGenerate={promptGenerateTrigger}
                                            onGeneratingChange={setGeneratingPrompt}
                                        />
                                    </div>
                                </div>
                                <div style={{
                                    flex: '3 3 60%',
                                    minWidth: screenSize === 'medium' ? '450px' : '500px',
                                    overflow: 'hidden'
                                }}>
                                    <PromptHistory
                                        apiKeyId={selectedApiKeyId}
                                        refreshTrigger={refreshTrigger}
                                        chainModalVisible={chainModalVisible}
                                        onOpenChainModal={(recordId) => {
                                            setSelectedRecordId(recordId);
                                            setChainModalVisible(true);
                                        }}
                                        onCloseChainModal={() => {
                                            setChainModalVisible(false);
                                            setSelectedRecordId(null);
                                        }}
                                        selectedRecordId={selectedRecordId}
                                        onTransferToChat={handleTransferToChat}
                                        publicView={true}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default PocBotView;
